<div class="divide-y divide-slate-200">

  <!-- ══ Cloud Platforms ══════════════════════════════════════════════════════ -->
  <div class="py-10 first:pt-0">
    <app-integration-category
      label="Cloud Platforms"
      description="Connect cloud accounts to monitor infrastructure, jobs, and pipelines."
    >

      <!-- ── AWS ───────────────────────────────────────────────────────────── -->
      <div class="space-y-5">
        <h3 class="text-sm font-semibold text-slate-700">AWS</h3>

        <!-- Success banner (shown after a successful test + connect) -->
        <div *ngIf="awsTestSuccess && awsState === 'connected'" class="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <lucide-icon name="circle-check" [size]="15" class="mt-0.5 flex-shrink-0 text-emerald-500" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-emerald-700">AWS connection verified and active.</p>
        </div>

        <!-- Test error banner -->
        <div *ngIf="awsTestError && awsState === 'not-connected'" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-red-700">{{ awsTestError }}</p>
        </div>

        <!-- Loading -->
        <div *ngIf="awsState === 'loading'" class="flex items-center gap-2 py-8 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading...
        </div>

        <!-- ── Not connected / read-only notice ──────────────────────────────── -->
        <div *ngIf="awsState === 'not-connected' && isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="flex items-center gap-4 px-6 py-5">
            <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-100">
              <lucide-icon name="cloud" [size]="19" class="text-orange-600" [strokeWidth]="1.75"></lucide-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-slate-900">AWS</p>
              <p class="mt-0.5 text-sm text-slate-500">Not connected. Contact your organization owner to set up the AWS integration.</p>
            </div>
          </div>
        </div>

        <!-- ── Not connected / setup form ──────────────────────────────────── -->
        <ng-container *ngIf="awsState === 'not-connected' && awsConnection && !isReadOnly">

          <!-- Setup guide -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <h2 class="text-sm font-semibold text-slate-900">How to set up AWS access</h2>
              <p class="mt-0.5 text-xs text-slate-500">
                KernalHQ uses the AWS cross-account IAM role pattern.
                <strong>Do not provide access keys.</strong>
                We use temporary credentials only.
              </p>
            </div>
            <div class="divide-y divide-slate-100 px-6 py-4 space-y-4 text-sm text-slate-700">
              <ol class="list-decimal list-inside space-y-2 text-sm text-slate-700">
                <li>In your AWS account, go to <strong>IAM → Roles → Create role</strong> and choose <em>Another AWS account</em> as the trusted entity.</li>
                <li>Enter KernalHQ's AWS Account ID as the Principal: <code class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700">{{ awsConnection.kernalHqAccountId || '(loading…)' }}</code></li>
                <li>Enable <strong>Require external ID</strong> and paste your unique External ID shown below.</li>
                <li>Attach the permissions policy for the services you want to monitor (Lambda, Glue, CodePipeline, etc.).</li>
                <li>Copy the resulting <strong>Role ARN</strong> and paste it into the form below.</li>
              </ol>

              <!-- Trust policy example -->
              <div class="pt-2">
                <p class="mb-1.5 text-xs font-semibold text-slate-600">Example trust policy (replace placeholders):</p>
                <pre class="overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 text-[11px] leading-relaxed text-slate-200 whitespace-pre-wrap">{{ awsTrustPolicyExample }}</pre>
              </div>

              <!-- Least-privilege starter policy -->
              <div class="pt-2">
                <p class="mb-1.5 text-xs font-semibold text-slate-600">Recommended minimum permissions policy (adjust to your needs):</p>
                <pre class="overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 text-[11px] leading-relaxed text-slate-200 whitespace-pre-wrap">{{ awsPermissionsPolicyExample }}</pre>
              </div>
            </div>
          </div>

          <!-- Connection form -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <h2 class="text-sm font-semibold text-slate-900">Connect your AWS account</h2>
            </div>
            <div class="space-y-5 px-6 py-5">

              <!-- External ID (read-only) -->
              <div>
                <label class="mb-1.5 block text-xs font-medium text-slate-700">
                  External ID <span class="text-slate-400">(generated by KernalHQ — required in trust policy)</span>
                </label>
                <div class="flex items-center gap-2">
                  <input
                    type="text"
                    readonly
                    [value]="awsConnection.externalId"
                    class="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-600 focus:outline-none"
                  />
                </div>
              </div>

              <!-- Role ARN -->
              <div>
                <label for="aws-role-arn" class="mb-1.5 block text-xs font-medium text-slate-700">IAM Role ARN</label>
                <input
                  id="aws-role-arn"
                  type="text"
                  [(ngModel)]="awsRoleArn"
                  placeholder="arn:aws:iam::123456789012:role/KernalHQReadRole"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
                />
              </div>

              <!-- Region -->
              <div>
                <label for="aws-region" class="mb-1.5 block text-xs font-medium text-slate-700">Primary Region</label>
                <select
                  id="aws-region"
                  [(ngModel)]="awsRegion"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <option value="us-east-1">us-east-1 (N. Virginia)</option>
                  <option value="us-east-2">us-east-2 (Ohio)</option>
                  <option value="us-west-1">us-west-1 (N. California)</option>
                  <option value="us-west-2">us-west-2 (Oregon)</option>
                  <option value="eu-west-1">eu-west-1 (Ireland)</option>
                  <option value="eu-west-2">eu-west-2 (London)</option>
                  <option value="eu-central-1">eu-central-1 (Frankfurt)</option>
                  <option value="ap-southeast-1">ap-southeast-1 (Singapore)</option>
                  <option value="ap-southeast-2">ap-southeast-2 (Sydney)</option>
                  <option value="ap-northeast-1">ap-northeast-1 (Tokyo)</option>
                </select>
              </div>

              <!-- Submit -->
              <button
                (click)="testConnectAws()"
                [disabled]="awsTestLoading || !awsRoleArn"
                class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="awsTestLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="!awsTestLoading" name="cloud" [size]="14" [strokeWidth]="2"></lucide-icon>
                {{ awsTestLoading ? 'Testing connection…' : 'Connect AWS' }}
              </button>
            </div>
          </div>

        </ng-container>

        <!-- ── Testing / loading state ────────────────────────────────────── -->
        <div *ngIf="awsState === 'testing'" class="flex items-center gap-2 py-8 text-sm text-slate-500">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Assuming role and verifying identity…
        </div>

        <!-- ── Connected ──────────────────────────────────────────────────── -->
        <ng-container *ngIf="awsState === 'connected' && awsConnection">

          <!-- AWS connection — cross-account role identity (no regions here;
               geography is managed per account below, mirroring Azure). -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-100 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">AWS connection</p>
            </div>
            <div class="flex items-center gap-4 px-6 py-5">
              <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-100">
                <lucide-icon name="cloud" [size]="19" class="text-orange-600" [strokeWidth]="1.75"></lucide-icon>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="truncate text-sm font-semibold text-slate-900">Cross-account role connected</p>
                  <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Active</span>
                </div>
                <p class="mt-0.5 text-xs text-slate-400">
                  {{ awsAccounts.length }} account{{ awsAccounts.length === 1 ? '' : 's' }} ·
                  primary {{ awsConnection.accountId || '—' }}
                </p>
              </div>
              <p class="flex-shrink-0 text-xs text-slate-400">Connected {{ awsConnection.connectedAt | date:'MMM d, y' }}</p>
            </div>
            <dl class="grid grid-cols-1 gap-px border-t border-slate-100 bg-slate-100 sm:grid-cols-2">
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Role ARN</dt>
                <dd class="mt-0.5 truncate font-mono text-xs text-slate-700">{{ awsConnection.roleArn }}</dd>
              </div>
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">External ID</dt>
                <dd class="mt-0.5 truncate font-mono text-xs text-slate-400">{{ awsConnection.externalId }}</dd>
              </div>
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Accounts</dt>
                <dd class="mt-0.5 text-xs text-slate-700">{{ awsAccounts.length }} connected</dd>
              </div>
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Primary account</dt>
                <dd class="mt-0.5 truncate font-mono text-xs text-slate-700">{{ awsConnection.accountId || '—' }}</dd>
              </div>
              <div class="bg-white px-6 py-3 sm:col-span-2">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Last verified</dt>
                <dd class="mt-0.5 text-xs text-slate-700">{{ awsConnection.lastTestedAt | date:'MMM d, y, h:mm a' }}</dd>
              </div>
            </dl>
            <p class="border-t border-slate-100 px-6 py-3 text-[11px] text-slate-400">
              KernalHQ assumes this role read-only using your external ID. Regions are enabled per account below —
              global services (e.g. CloudFront) are always covered.
            </p>
          </div>

          <!-- AWS accounts -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="flex items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
              <div>
                <h2 class="text-sm font-semibold text-slate-900">AWS accounts</h2>
                <p class="mt-0.5 text-xs text-slate-500">{{ awsAccounts.length }} account{{ awsAccounts.length === 1 ? '' : 's' }} connected under this cross-account role. Regions are enabled per account.</p>
              </div>
              <button
                *ngIf="!isReadOnly && !awsAddingAccount"
                type="button"
                (click)="startAddAwsAccount()"
                class="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
              >
                <lucide-icon name="plus" [size]="13" [strokeWidth]="2"></lucide-icon>
                Add AWS account
              </button>
            </div>
            <ul class="divide-y divide-slate-100">
              <li *ngFor="let a of awsAccounts; trackBy: trackByAwsAccountId" class="px-6 py-3">
                <div class="flex items-center justify-between gap-3">
                  <span class="flex min-w-0 items-center gap-2">
                    <lucide-icon name="cloud" [size]="14" class="flex-shrink-0 text-slate-400" [strokeWidth]="1.75"></lucide-icon>
                    <span class="min-w-0">
                      <span class="flex items-center gap-2">
                        <span class="truncate text-sm font-medium text-slate-800">{{ a.label || a.accountId }}</span>
                        <span *ngIf="a.isPrimary" class="flex-shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700" title="Primary account">Primary</span>
                      </span>
                      <span class="block truncate font-mono text-[11px] text-slate-400">{{ a.accountId }}</span>
                    </span>
                  </span>
                  <div class="flex flex-shrink-0 items-center gap-3">
                    <span class="hidden text-xs text-slate-400 sm:inline">{{ a.regionCount }} region{{ a.regionCount === 1 ? '' : 's' }}</span>
                    <!-- Per-account controls — regions belong to the selected account,
                         so the geography editor is available for every account. -->
                    <ng-container *ngIf="!isReadOnly">
                      <button
                        type="button"
                        (click)="awsEditRegionsAccountId === a.accountId ? cancelEditAccountRegions() : startEditAccountRegions(a)"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                        [attr.aria-expanded]="awsEditRegionsAccountId === a.accountId"
                      >Manage regions</button>
                      <button
                        type="button"
                        (click)="askDisconnectAccount(a)"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-400 transition-colors hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                      >Disconnect</button>
                    </ng-container>
                  </div>
                </div>

                <!-- Current regions, inline (default marked) -->
                <div class="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
                  <span
                    *ngFor="let code of a.enabledRegions"
                    class="rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
                    [class.border-violet-200]="code === a.defaultRegion"
                    [class.bg-violet-50]="code === a.defaultRegion"
                    [class.text-violet-700]="code === a.defaultRegion"
                    [class.border-slate-200]="code !== a.defaultRegion"
                    [class.text-slate-500]="code !== a.defaultRegion"
                  >{{ code }}<span *ngIf="code === a.defaultRegion"> · default</span></span>
                  <span *ngIf="!(a.enabledRegions && a.enabledRegions.length)" class="text-[11px] text-slate-400">No regions enabled</span>
                </div>

                <!-- Inline per-account region editor (geography scoped to the account) -->
                <div *ngIf="awsEditRegionsAccountId === a.accountId" class="mt-3">
                  <app-geography-scope-editor
                    [items]="supportedRegions"
                    [enabled]="awsAcctEnabledRegions"
                    [defaultCode]="awsAcctDefaultRegion"
                    (defaultCodeChange)="awsAcctDefaultRegion = $event"
                    geoNoun="region"
                    geoNounPlural="regions"
                    [title]="'Regions enabled for this account'"
                    [description]="'KernalHQ discovers and monitors resources in these AWS regions for ' + (a.label || a.accountId) + '.'"
                    [selectId]="'aws-acct-default-' + a.accountId"
                    [saving]="awsAcctRegionsSaving"
                    [error]="awsAcctRegionsError"
                    (toggle)="toggleAccountRegion($event)"
                    (save)="saveAccountRegions()"
                    (cancel)="cancelEditAccountRegions()"
                  ></app-geography-scope-editor>
                </div>
              </li>
            </ul>

            <!-- Add-additional-account form. Isolated from the primary connect
                 flow: a failure here never destabilises the accounts above. -->
            <div *ngIf="awsAddingAccount" class="space-y-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <p class="text-xs text-slate-500">
                Create a cross-account IAM role in the AWS account you want to add, using the
                <strong>same trust policy</strong> shown above (same KernalHQ account ID and external ID),
                then paste its role ARN below.
              </p>

              <div *ngIf="awsAddError" class="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2" role="alert">
                <lucide-icon name="triangle-alert" [size]="14" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
                <p class="text-xs text-red-700">{{ awsAddError }}</p>
              </div>

              <div>
                <label for="aws-add-role" class="mb-1 block text-xs font-medium text-slate-600">Role ARN</label>
                <input
                  id="aws-add-role"
                  type="text"
                  [(ngModel)]="awsAddRoleArn"
                  [disabled]="awsAddLoading"
                  placeholder="arn:aws:iam::222222222222:role/KernalHQReadRole"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 disabled:opacity-60"
                />
              </div>

              <div class="flex flex-wrap items-end gap-3">
                <div>
                  <label for="aws-add-region" class="mb-1 block text-xs font-medium text-slate-600">Verification region</label>
                  <select
                    id="aws-add-region"
                    [(ngModel)]="awsAddRegion"
                    [disabled]="awsAddLoading"
                    class="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 disabled:opacity-60"
                  >
                    <option *ngFor="let r of supportedRegions" [value]="r.code">{{ r.code }}</option>
                  </select>
                </div>
                <div class="flex-1 min-w-[10rem]">
                  <label for="aws-add-name" class="mb-1 block text-xs font-medium text-slate-600">Display name <span class="text-slate-400">(optional)</span></label>
                  <input
                    id="aws-add-name"
                    type="text"
                    [(ngModel)]="awsAddDisplayName"
                    [disabled]="awsAddLoading"
                    placeholder="e.g. Production"
                    class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 disabled:opacity-60"
                  />
                </div>
              </div>

              <div class="flex items-center gap-2">
                <button
                  type="button"
                  (click)="addAwsAccount()"
                  [disabled]="awsAddLoading || !awsAddRoleArn.trim()"
                  class="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <lucide-icon *ngIf="awsAddLoading" name="loader" [size]="13" [strokeWidth]="2" class="animate-spin"></lucide-icon>
                  {{ awsAddLoading ? 'Verifying…' : 'Verify & add account' }}
                </button>
                <button
                  type="button"
                  (click)="cancelAddAwsAccount()"
                  [disabled]="awsAddLoading"
                  class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Disconnect-account confirmation dialog -->
          <div *ngIf="awsConfirmDisconnect" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4" role="dialog" aria-modal="true" aria-labelledby="aws-disconnect-title">
            <div class="w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
              <div class="flex items-start gap-3 px-6 py-5">
                <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                  <lucide-icon name="triangle-alert" [size]="18" class="text-red-600" [strokeWidth]="2"></lucide-icon>
                </div>
                <div class="min-w-0">
                  <h3 id="aws-disconnect-title" class="text-sm font-semibold text-slate-900">
                    Disconnect AWS account {{ awsConfirmDisconnect.label || awsConfirmDisconnect.accountId }}?
                  </h3>
                  <p class="mt-1.5 text-sm text-slate-500">
                    KernalHQ will stop monitoring this account and remove its monitored services, alert
                    routing, and open findings — freeing those monitored-resource slots. Other connected
                    accounts are unaffected.
                  </p>
                  <p *ngIf="awsConfirmDisconnect.isPrimary" class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    This is your <strong>primary</strong> account. Another connected account will
                    automatically be promoted to primary; your subscription's external ID is unchanged.
                  </p>
                </div>
              </div>
              <div class="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-3">
                <button
                  type="button"
                  (click)="cancelDisconnectAccount()"
                  [disabled]="awsDisconnectAccountLoading"
                  class="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
                >Cancel</button>
                <button
                  type="button"
                  (click)="confirmDisconnectAccount()"
                  [disabled]="awsDisconnectAccountLoading"
                  class="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <lucide-icon *ngIf="awsDisconnectAccountLoading" name="loader" [size]="13" [strokeWidth]="2" class="animate-spin"></lucide-icon>
                  {{ awsDisconnectAccountLoading ? 'Disconnecting…' : 'Disconnect account' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Required IAM Policy -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <button
              (click)="awsPolicyExpanded = !awsPolicyExpanded"
              class="flex w-full items-center justify-between px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-300"
              [attr.aria-expanded]="awsPolicyExpanded"
            >
              <div>
                <h2 class="text-sm font-semibold text-slate-900">Required IAM Policy</h2>
                <p class="mt-0.5 text-xs text-slate-500">View the permissions your role must have. Update your policy if you've added new features.</p>
              </div>
              <lucide-icon
                name="chevron-down"
                [size]="16"
                class="ml-4 flex-shrink-0 text-slate-400 transition-transform"
                [class.rotate-180]="awsPolicyExpanded"
                [strokeWidth]="2"
              ></lucide-icon>
            </button>

            <div *ngIf="awsPolicyExpanded" class="border-t border-slate-100 px-6 pb-5 pt-4">
              <div class="mb-3 flex items-center justify-between">
                <p class="text-xs text-slate-500">Copy this policy and attach it to your IAM role in AWS.</p>
                <button
                  (click)="copyPolicy()"
                  class="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.border-emerald-300]="awsCopySuccess"
                  [class.bg-emerald-50]="awsCopySuccess"
                  [class.text-emerald-700]="awsCopySuccess"
                >
                  <lucide-icon
                    [name]="awsCopySuccess ? 'circle-check' : 'copy'"
                    [size]="12"
                    [strokeWidth]="2"
                  ></lucide-icon>
                  {{ awsCopySuccess ? 'Copied!' : 'Copy' }}
                </button>
              </div>
              <pre class="overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 text-[11px] leading-relaxed text-slate-200 whitespace-pre-wrap">{{ awsPermissionsPolicyExample }}</pre>
            </div>
          </div>

          <!-- Danger zone -->
          <div *ngIf="!isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-red-400">Danger Zone</p>
            </div>
            <div class="px-6 py-5">
              <p class="mb-4 text-sm text-slate-600">
                Removing the connection deletes the stored Role ARN from KernalHQ. KernalHQ will no longer be able to access your AWS account.
                You may also remove the IAM role from your AWS account if you no longer need it.
              </p>
              <button
                (click)="disconnectAws()"
                [disabled]="awsDisconnectLoading"
                class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="awsDisconnectLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="!awsDisconnectLoading" name="unlink" [size]="14" [strokeWidth]="1.75"></lucide-icon>
                {{ awsDisconnectLoading ? 'Disconnecting…' : 'Disconnect AWS' }}
              </button>
            </div>
          </div>

        </ng-container>

        <!-- ── Error state ────────────────────────────────────────────────── -->
        <div *ngIf="awsState === 'error'" class="overflow-hidden rounded-xl border border-red-200 bg-red-50 px-6 py-5 text-sm text-red-700 shadow-sm">
          Failed to load AWS integration. Please refresh the page.
        </div>

      </div>

      <!-- ── Azure ──────────────────────────────────────────────────────────── -->
      <div class="mt-8 space-y-5 border-t border-slate-200 pt-8">
        <h3 class="text-sm font-semibold text-slate-700">Azure</h3>

        <!-- Success / failure banners -->
        <div *ngIf="azureTestSuccess && azureState === 'connected'" class="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <lucide-icon name="circle-check" [size]="16" class="mt-0.5 flex-shrink-0 text-emerald-600" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-emerald-700">Azure subscription connected and active.</p>
        </div>
        <div *ngIf="azureTestError" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <lucide-icon name="triangle-alert" [size]="16" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-red-700">{{ azureTestError }}</p>
        </div>

        <!-- Loading -->
        <div *ngIf="azureState === 'loading'" class="flex items-center gap-2 py-8 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading Azure integration…
        </div>

        <!-- Read-only member, not connected -->
        <div *ngIf="azureState === 'not-connected' && isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="flex items-center gap-4 px-6 py-5">
            <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100">
              <lucide-icon name="cloud" [size]="20" class="text-blue-600" [strokeWidth]="1.75"></lucide-icon>
            </div>
            <div>
              <p class="text-sm font-semibold text-slate-900">Azure</p>
              <p class="mt-0.5 text-sm text-slate-500">Not connected. Contact your organization owner to set up the Azure integration.</p>
            </div>
          </div>
        </div>

        <!-- Connect form (owner/admin) -->
        <div *ngIf="azureState === 'not-connected' && !isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="border-b border-slate-200 px-6 py-4">
            <h2 class="text-sm font-semibold text-slate-900">Connect Azure</h2>
            <p class="mt-0.5 text-xs text-slate-500">Configure an Azure service principal (app registration) with <strong>Reader</strong> access to the subscription. We store no user passwords.</p>
          </div>
          <div class="space-y-5 px-6 py-5">

            <div>
              <label for="azure-tenant" class="mb-1.5 block text-xs font-medium text-slate-700">Tenant ID</label>
              <input id="azure-tenant" type="text" [(ngModel)]="azureTenantId"
                placeholder="aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
            </div>

            <div>
              <label for="azure-subscription" class="mb-1.5 block text-xs font-medium text-slate-700">Subscription ID</label>
              <input id="azure-subscription" type="text" [(ngModel)]="azureSubscriptionId"
                placeholder="11111111-1111-1111-1111-111111111111"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
            </div>

            <div>
              <label for="azure-client" class="mb-1.5 block text-xs font-medium text-slate-700">Client ID <span class="text-slate-400">(application ID)</span></label>
              <input id="azure-client" type="text" [(ngModel)]="azureClientId"
                placeholder="bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
            </div>

            <div>
              <label for="azure-secret" class="mb-1.5 block text-xs font-medium text-slate-700">Client secret</label>
              <input id="azure-secret" type="password" autocomplete="off" [(ngModel)]="azureClientSecret"
                placeholder="••••••••••••••••"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
              <p class="mt-1 text-[11px] text-slate-400">The secret is used to verify the connection and is not stored in plaintext.</p>
            </div>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label for="azure-location" class="mb-1.5 block text-xs font-medium text-slate-700">Primary location</label>
                <select id="azure-location" [(ngModel)]="azureDefaultLocation"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200">
                  <option *ngFor="let l of supportedLocations" [value]="l.code">{{ l.code }} ({{ l.label }})</option>
                </select>
              </div>
              <div>
                <label for="azure-display" class="mb-1.5 block text-xs font-medium text-slate-700">Display name <span class="text-slate-400">(optional)</span></label>
                <input id="azure-display" type="text" [(ngModel)]="azureDisplayName"
                  placeholder="Production"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
              </div>
            </div>

            <button
              (click)="testConnectAzure()"
              [disabled]="azureTestLoading || !azureTenantId || !azureSubscriptionId || !azureClientId || !azureClientSecret"
              class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <lucide-icon *ngIf="azureTestLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
              <lucide-icon *ngIf="!azureTestLoading" name="cloud" [size]="14" [strokeWidth]="2"></lucide-icon>
              {{ azureTestLoading ? 'Testing connection…' : 'Test connection' }}
            </button>
          </div>
        </div>

        <!-- Testing -->
        <div *ngIf="azureState === 'testing'" class="flex items-center gap-2 py-8 text-sm text-slate-500">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Verifying the service principal…
        </div>

        <!-- Connected -->
        <ng-container *ngIf="azureState === 'connected' && azureConnection">

          <!-- Connection details — service-principal context (no raw secrets). -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-100 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Azure connection</p>
            </div>
            <div class="flex items-center gap-4 px-6 py-5">
              <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <lucide-icon name="cloud" [size]="19" class="text-blue-600" [strokeWidth]="1.75"></lucide-icon>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="truncate text-sm font-semibold text-slate-900">Service principal connected</p>
                  <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Active</span>
                </div>
                <p class="mt-0.5 text-xs text-slate-400">
                  {{ azureConnection.subscriptionCount }} subscription{{ azureConnection.subscriptionCount === 1 ? '' : 's' }} ·
                  primary {{ azurePrimarySubscriptionId || '—' }}
                </p>
              </div>
            </div>
            <dl class="grid grid-cols-1 gap-px border-t border-slate-100 bg-slate-100 sm:grid-cols-2">
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Tenant ID</dt>
                <dd class="mt-0.5 truncate font-mono text-xs text-slate-700">{{ azureConnection.tenantId || '—' }}</dd>
              </div>
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Client (application) ID</dt>
                <dd class="mt-0.5 truncate font-mono text-xs text-slate-700">{{ azureConnection.clientId || '—' }}</dd>
              </div>
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Subscriptions</dt>
                <dd class="mt-0.5 text-xs text-slate-700">{{ azureConnection.subscriptionCount }} connected</dd>
              </div>
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Primary subscription</dt>
                <dd class="mt-0.5 truncate font-mono text-xs text-slate-700">{{ azurePrimarySubscriptionId || '—' }}</dd>
              </div>
            </dl>
            <p class="border-t border-slate-100 px-6 py-3 text-[11px] text-slate-400">
              The service principal needs the <strong>Reader</strong> role on each connected subscription. The client
              secret is stored encrypted and is never displayed here.
            </p>
          </div>

          <!-- Subscriptions + per-subscription location management -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="flex items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
              <div>
                <h2 class="text-sm font-semibold text-slate-900">Azure subscriptions</h2>
                <p class="mt-0.5 text-xs text-slate-500">{{ azureConnection.subscriptionCount }} subscription{{ azureConnection.subscriptionCount === 1 ? '' : 's' }} connected via this service principal.</p>
              </div>
            </div>
            <ul class="divide-y divide-slate-100">
              <li *ngFor="let s of azureSubscriptions; trackBy: trackByAzureSubscriptionId" class="px-6 py-3">
                <div class="flex items-center justify-between gap-3">
                  <span class="flex min-w-0 items-center gap-2">
                    <lucide-icon name="cloud" [size]="14" class="flex-shrink-0 text-slate-400" [strokeWidth]="1.75"></lucide-icon>
                    <span class="min-w-0">
                      <span class="flex items-center gap-2">
                        <span class="truncate text-sm font-medium text-slate-800">{{ s.label || s.subscriptionId }}</span>
                        <span *ngIf="s.isPrimary" class="flex-shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700" title="Primary subscription">Primary</span>
                      </span>
                      <span class="block truncate font-mono text-[11px] text-slate-400">{{ s.subscriptionId }}</span>
                      <span class="block text-[11px] text-slate-400">
                        <span *ngIf="s.lastTestedAt">Last verified {{ s.lastTestedAt | date:'MMM d, y, h:mm a' }}</span>
                        <span *ngIf="!s.lastTestedAt">Not verified yet</span>
                      </span>
                    </span>
                  </span>
                  <div class="flex flex-shrink-0 items-center gap-3">
                    <span class="hidden text-xs text-slate-400 sm:inline">{{ (s.enabledLocations || []).length }} location{{ (s.enabledLocations || []).length === 1 ? '' : 's' }}</span>
                    <span class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      [class.bg-emerald-50]="s.connectionStatus === 'ACTIVE'" [class.text-emerald-700]="s.connectionStatus === 'ACTIVE'"
                      [class.bg-amber-50]="s.connectionStatus !== 'ACTIVE'" [class.text-amber-700]="s.connectionStatus !== 'ACTIVE'">
                      {{ s.connectionStatus === 'ACTIVE' ? 'Active' : (s.connectionStatus || 'Pending') }}
                    </span>
                    <ng-container *ngIf="!isReadOnly">
                      <button
                        type="button"
                        (click)="verifyAzureSubscription(s)"
                        [disabled]="azureVerifyingSubId === s.subscriptionId"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
                      >{{ azureVerifyingSubId === s.subscriptionId ? 'Verifying…' : 'Verify' }}</button>
                      <button
                        *ngIf="!s.isPrimary && s.connectionStatus === 'ACTIVE'"
                        type="button"
                        (click)="makePrimaryAzureSubscription(s)"
                        [disabled]="azureMakingPrimarySubId === s.subscriptionId"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
                      >{{ azureMakingPrimarySubId === s.subscriptionId ? 'Setting…' : 'Make primary' }}</button>
                      <button
                        type="button"
                        (click)="azureEditLocationsSubId === s.subscriptionId ? cancelEditAzureLocations() : startEditAzureLocations(s)"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                        [attr.aria-expanded]="azureEditLocationsSubId === s.subscriptionId"
                      >Manage locations</button>
                      <button
                        type="button"
                        (click)="askDisconnectAzureSubscription(s)"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-400 transition-colors hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                      >Disconnect</button>
                    </ng-container>
                  </div>
                </div>

                <!-- Current locations, inline -->
                <div class="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
                  <span
                    *ngFor="let code of s.enabledLocations"
                    class="rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
                    [class.border-violet-200]="code === (s.defaultLocation || s.enabledLocations[0])"
                    [class.bg-violet-50]="code === (s.defaultLocation || s.enabledLocations[0])"
                    [class.text-violet-700]="code === (s.defaultLocation || s.enabledLocations[0])"
                    [class.border-slate-200]="code !== (s.defaultLocation || s.enabledLocations[0])"
                    [class.text-slate-500]="code !== (s.defaultLocation || s.enabledLocations[0])"
                    [title]="locationLabel(code)"
                  >{{ code }}<span *ngIf="code === (s.defaultLocation || s.enabledLocations[0])"> · default</span></span>
                  <span *ngIf="!(s.enabledLocations && s.enabledLocations.length)" class="text-[11px] text-slate-400">No locations enabled</span>
                </div>

                <!-- Inline per-subscription disconnect confirmation -->
                <div *ngIf="azureConfirmDisconnectSub?.subscriptionId === s.subscriptionId" class="mt-3 space-y-3 rounded-lg border border-red-200 bg-red-50 p-3">
                  <p class="text-xs text-red-700">
                    Disconnect <span class="font-mono font-medium">{{ s.label || s.subscriptionId }}</span>? Its monitored
                    resources stop being checked.
                    <span *ngIf="s.isPrimary">Another connected subscription is promoted to primary automatically.</span>
                  </p>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      (click)="confirmDisconnectAzureSubscription()"
                      [disabled]="azureDisconnectSubLoading"
                      class="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >{{ azureDisconnectSubLoading ? 'Disconnecting…' : 'Disconnect subscription' }}</button>
                    <button
                      type="button"
                      (click)="cancelDisconnectAzureSubscription()"
                      [disabled]="azureDisconnectSubLoading"
                      class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
                    >Cancel</button>
                  </div>
                </div>

                <!-- Inline per-subscription location editor (geography scoped to the subscription) -->
                <div *ngIf="azureEditLocationsSubId === s.subscriptionId" class="mt-3">
                  <app-geography-scope-editor
                    [items]="supportedLocations"
                    [enabled]="azureLocEnabled"
                    [defaultCode]="azureLocDefault"
                    (defaultCodeChange)="azureLocDefault = $event"
                    geoNoun="location"
                    geoNounPlural="locations"
                    [title]="'Locations enabled for this subscription'"
                    [description]="'KernalHQ discovers and monitors resources in these Azure locations for ' + (s.label || s.subscriptionId) + '.'"
                    [selectId]="'azure-loc-default-' + s.subscriptionId"
                    [saving]="azureLocSaving"
                    [error]="azureLocError"
                    (toggle)="toggleAzureLocation($event)"
                    (save)="saveAzureLocations()"
                    (cancel)="cancelEditAzureLocations()"
                  ></app-geography-scope-editor>
                </div>
              </li>
            </ul>
            <p class="border-t border-slate-100 px-6 py-3 text-[11px] text-slate-400">
              Locations decide which Azure regions KernalHQ discovers and monitors resources in for a subscription. The
              default location is used when a resource doesn't specify one. Global services (e.g. Front Door) are always
              covered regardless of the enabled locations.
            </p>
          </div>

          <!-- Add another subscription -->
          <div *ngIf="!isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <h2 class="text-sm font-semibold text-slate-900">Add another subscription</h2>
              <p class="mt-0.5 text-xs text-slate-500">Grant the same (or another) service principal Reader access to an additional subscription.</p>
            </div>
            <div class="space-y-4 px-6 py-5">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input type="text" [(ngModel)]="azureSubscriptionId" placeholder="Subscription ID"
                  class="rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
                <input type="password" autocomplete="off" [(ngModel)]="azureClientSecret" placeholder="Client secret"
                  class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
              </div>
              <button
                (click)="testConnectAzure()"
                [disabled]="azureTestLoading || !azureSubscriptionId || !azureClientSecret"
                class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="azureTestLoading" name="loader" [size]="13" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="!azureTestLoading" name="plus" [size]="13" [strokeWidth]="2"></lucide-icon>
                Add subscription
              </button>
            </div>
          </div>

          <!-- Danger zone -->
          <div *ngIf="!isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-red-400">Danger Zone</p>
            </div>
            <div class="px-6 py-5">
              <p class="mb-4 text-sm text-slate-600">Removing the connection deletes every stored Azure subscription and the service-principal reference from KernalHQ.</p>
              <button
                (click)="disconnectAzure()"
                [disabled]="azureDisconnectLoading"
                class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="azureDisconnectLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="!azureDisconnectLoading" name="unlink" [size]="14" [strokeWidth]="1.75"></lucide-icon>
                {{ azureDisconnectLoading ? 'Disconnecting…' : 'Disconnect Azure' }}
              </button>
            </div>
          </div>
        </ng-container>

        <!-- Error -->
        <div *ngIf="azureState === 'error'" class="overflow-hidden rounded-xl border border-red-200 bg-red-50 px-6 py-5 text-sm text-red-700 shadow-sm">
          Failed to load the Azure integration. Please refresh the page.
        </div>
      </div>

      <!-- ── Google Cloud ───────────────────────────────────────────────────── -->
      <div class="mt-8 space-y-5 border-t border-slate-200 pt-8">
        <h3 class="text-sm font-semibold text-slate-700">Google Cloud</h3>

        <!-- Success / failure banners -->
        <div *ngIf="gcpTestSuccess && gcpState === 'connected'" class="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <lucide-icon name="circle-check" [size]="16" class="mt-0.5 flex-shrink-0 text-emerald-600" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-emerald-700">Google Cloud project connected and active.</p>
        </div>
        <div *ngIf="gcpTestError" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <lucide-icon name="triangle-alert" [size]="16" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-red-700">{{ gcpTestError }}</p>
        </div>

        <!-- Loading -->
        <div *ngIf="gcpState === 'loading'" class="flex items-center gap-2 py-8 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading Google Cloud integration…
        </div>

        <!-- Read-only member, not connected -->
        <div *ngIf="gcpState === 'not-connected' && isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="flex items-center gap-4 px-6 py-5">
            <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100">
              <lucide-icon name="cloud" [size]="20" class="text-blue-600" [strokeWidth]="1.75"></lucide-icon>
            </div>
            <div>
              <p class="text-sm font-semibold text-slate-900">Google Cloud</p>
              <p class="mt-0.5 text-sm text-slate-500">Not connected. Contact your organization owner to set up the Google Cloud integration.</p>
            </div>
          </div>
        </div>

        <!-- Connect form (owner/admin) -->
        <div *ngIf="gcpState === 'not-connected' && !isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="border-b border-slate-200 px-6 py-4">
            <h2 class="text-sm font-semibold text-slate-900">Connect Google Cloud</h2>
            <p class="mt-0.5 text-xs text-slate-500">Create a Google Cloud service account with the <strong>Viewer</strong> role on the project, download its key JSON, and paste it below. We store no user passwords.</p>
          </div>
          <div class="space-y-5 px-6 py-5">

            <div>
              <label for="gcp-project" class="mb-1.5 block text-xs font-medium text-slate-700">Project ID</label>
              <input id="gcp-project" type="text" [(ngModel)]="gcpProjectId"
                placeholder="my-gcp-project"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
            </div>

            <div>
              <label for="gcp-key" class="mb-1.5 block text-xs font-medium text-slate-700">Service account key JSON</label>
              <textarea id="gcp-key" rows="6" autocomplete="off" [(ngModel)]="gcpServiceAccountKey"
                placeholder='{ "type": "service_account", "project_id": "…", "private_key": "…", "client_email": "…" }'
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"></textarea>
              <p class="mt-1 text-[11px] text-slate-400">The key is used to verify the connection and is stored encrypted — never displayed here.</p>
            </div>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label for="gcp-region" class="mb-1.5 block text-xs font-medium text-slate-700">Primary region</label>
                <select id="gcp-region" [(ngModel)]="gcpDefaultRegion"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200">
                  <option *ngFor="let r of supportedGcpRegions" [value]="r.code">{{ r.code }} ({{ r.label }})</option>
                </select>
              </div>
              <div>
                <label for="gcp-display" class="mb-1.5 block text-xs font-medium text-slate-700">Display name <span class="text-slate-400">(optional)</span></label>
                <input id="gcp-display" type="text" [(ngModel)]="gcpDisplayName"
                  placeholder="Production"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
              </div>
            </div>

            <button
              (click)="testConnectGcp()"
              [disabled]="gcpTestLoading || !gcpProjectId || !gcpServiceAccountKey"
              class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <lucide-icon *ngIf="gcpTestLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
              <lucide-icon *ngIf="!gcpTestLoading" name="cloud" [size]="14" [strokeWidth]="2"></lucide-icon>
              {{ gcpTestLoading ? 'Testing connection…' : 'Test connection' }}
            </button>
          </div>
        </div>

        <!-- Testing -->
        <div *ngIf="gcpState === 'testing'" class="flex items-center gap-2 py-8 text-sm text-slate-500">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Verifying the service account…
        </div>

        <!-- Connected -->
        <ng-container *ngIf="gcpState === 'connected' && gcpConnection">

          <!-- Connection details — service-account context (no raw keys). -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-100 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Google Cloud connection</p>
            </div>
            <div class="flex items-center gap-4 px-6 py-5">
              <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <lucide-icon name="cloud" [size]="19" class="text-blue-600" [strokeWidth]="1.75"></lucide-icon>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="truncate text-sm font-semibold text-slate-900">Service account connected</p>
                  <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Active</span>
                </div>
                <p class="mt-0.5 text-xs text-slate-400">
                  {{ gcpConnection.projectCount }} project{{ gcpConnection.projectCount === 1 ? '' : 's' }} ·
                  primary {{ gcpPrimaryProjectId || '—' }}
                </p>
              </div>
            </div>
            <dl class="grid grid-cols-1 gap-px border-t border-slate-100 bg-slate-100 sm:grid-cols-2">
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Service account email</dt>
                <dd class="mt-0.5 truncate font-mono text-xs text-slate-700">{{ gcpConnection.clientEmail || '—' }}</dd>
              </div>
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Projects</dt>
                <dd class="mt-0.5 text-xs text-slate-700">{{ gcpConnection.projectCount }} connected</dd>
              </div>
              <div class="bg-white px-6 py-3">
                <dt class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Primary project</dt>
                <dd class="mt-0.5 truncate font-mono text-xs text-slate-700">{{ gcpPrimaryProjectId || '—' }}</dd>
              </div>
            </dl>
            <p class="border-t border-slate-100 px-6 py-3 text-[11px] text-slate-400">
              The service account needs the <strong>Viewer</strong> role on each connected project. The key is stored
              encrypted and is never displayed here.
            </p>
          </div>

          <!-- Projects + per-project region management -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="flex items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
              <div>
                <h2 class="text-sm font-semibold text-slate-900">Google Cloud projects</h2>
                <p class="mt-0.5 text-xs text-slate-500">{{ gcpConnection.projectCount }} project{{ gcpConnection.projectCount === 1 ? '' : 's' }} connected via this service account.</p>
              </div>
            </div>
            <ul class="divide-y divide-slate-100">
              <li *ngFor="let p of gcpProjects; trackBy: trackByGcpProjectId" class="px-6 py-3">
                <div class="flex items-center justify-between gap-3">
                  <span class="flex min-w-0 items-center gap-2">
                    <lucide-icon name="cloud" [size]="14" class="flex-shrink-0 text-slate-400" [strokeWidth]="1.75"></lucide-icon>
                    <span class="min-w-0">
                      <span class="flex items-center gap-2">
                        <span class="truncate text-sm font-medium text-slate-800">{{ p.label || p.projectId }}</span>
                        <span *ngIf="p.isPrimary" class="flex-shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700" title="Primary project">Primary</span>
                      </span>
                      <span class="block truncate font-mono text-[11px] text-slate-400">{{ p.projectId }}</span>
                      <span class="block text-[11px] text-slate-400">
                        <span *ngIf="p.lastTestedAt">Last verified {{ p.lastTestedAt | date:'MMM d, y, h:mm a' }}</span>
                        <span *ngIf="!p.lastTestedAt">Not verified yet</span>
                      </span>
                    </span>
                  </span>
                  <div class="flex flex-shrink-0 items-center gap-3">
                    <span class="hidden text-xs text-slate-400 sm:inline">{{ (p.enabledRegions || []).length }} region{{ (p.enabledRegions || []).length === 1 ? '' : 's' }}</span>
                    <span class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      [class.bg-emerald-50]="p.connectionStatus === 'ACTIVE'" [class.text-emerald-700]="p.connectionStatus === 'ACTIVE'"
                      [class.bg-amber-50]="p.connectionStatus !== 'ACTIVE'" [class.text-amber-700]="p.connectionStatus !== 'ACTIVE'">
                      {{ p.connectionStatus === 'ACTIVE' ? 'Active' : (p.connectionStatus || 'Pending') }}
                    </span>
                    <ng-container *ngIf="!isReadOnly">
                      <button
                        type="button"
                        (click)="verifyGcpProject(p)"
                        [disabled]="gcpVerifyingProjectId === p.projectId"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
                      >{{ gcpVerifyingProjectId === p.projectId ? 'Verifying…' : 'Verify' }}</button>
                      <button
                        *ngIf="!p.isPrimary && p.connectionStatus === 'ACTIVE'"
                        type="button"
                        (click)="makePrimaryGcpProject(p)"
                        [disabled]="gcpMakingPrimaryProjectId === p.projectId"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
                      >{{ gcpMakingPrimaryProjectId === p.projectId ? 'Setting…' : 'Make primary' }}</button>
                      <button
                        type="button"
                        (click)="gcpEditRegionsProjectId === p.projectId ? cancelEditGcpRegions() : startEditGcpRegions(p)"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                        [attr.aria-expanded]="gcpEditRegionsProjectId === p.projectId"
                      >Manage regions</button>
                      <button
                        type="button"
                        (click)="askDisconnectGcpProject(p)"
                        class="rounded-md px-1.5 py-1 text-xs font-medium text-slate-400 transition-colors hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                      >Disconnect</button>
                    </ng-container>
                  </div>
                </div>

                <!-- Current regions, inline -->
                <div class="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
                  <span
                    *ngFor="let code of p.enabledRegions"
                    class="rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
                    [class.border-violet-200]="code === (p.defaultRegion || p.enabledRegions[0])"
                    [class.bg-violet-50]="code === (p.defaultRegion || p.enabledRegions[0])"
                    [class.text-violet-700]="code === (p.defaultRegion || p.enabledRegions[0])"
                    [class.border-slate-200]="code !== (p.defaultRegion || p.enabledRegions[0])"
                    [class.text-slate-500]="code !== (p.defaultRegion || p.enabledRegions[0])"
                    [title]="regionLabel(code)"
                  >{{ code }}<span *ngIf="code === (p.defaultRegion || p.enabledRegions[0])"> · default</span></span>
                  <span *ngIf="!(p.enabledRegions && p.enabledRegions.length)" class="text-[11px] text-slate-400">No regions enabled</span>
                </div>

                <!-- Inline per-project disconnect confirmation -->
                <div *ngIf="gcpConfirmDisconnectProject?.projectId === p.projectId" class="mt-3 space-y-3 rounded-lg border border-red-200 bg-red-50 p-3">
                  <p class="text-xs text-red-700">
                    Disconnect <span class="font-mono font-medium">{{ p.label || p.projectId }}</span>? Its monitored
                    resources stop being checked.
                    <span *ngIf="p.isPrimary">Another connected project is promoted to primary automatically.</span>
                  </p>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      (click)="confirmDisconnectGcpProject()"
                      [disabled]="gcpDisconnectProjectLoading"
                      class="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >{{ gcpDisconnectProjectLoading ? 'Disconnecting…' : 'Disconnect project' }}</button>
                    <button
                      type="button"
                      (click)="cancelDisconnectGcpProject()"
                      [disabled]="gcpDisconnectProjectLoading"
                      class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-60"
                    >Cancel</button>
                  </div>
                </div>

                <!-- Inline per-project region editor (geography scoped to the project) -->
                <div *ngIf="gcpEditRegionsProjectId === p.projectId" class="mt-3">
                  <app-geography-scope-editor
                    [items]="supportedGcpRegions"
                    [enabled]="gcpRegEnabled"
                    [defaultCode]="gcpRegDefault"
                    (defaultCodeChange)="gcpRegDefault = $event"
                    geoNoun="region"
                    geoNounPlural="regions"
                    [title]="'Regions enabled for this project'"
                    [description]="'KernalHQ discovers and monitors resources in these GCP regions for ' + (p.label || p.projectId) + '.'"
                    [selectId]="'gcp-region-default-' + p.projectId"
                    [saving]="gcpRegSaving"
                    [error]="gcpRegError"
                    (toggle)="toggleGcpRegion($event)"
                    (save)="saveGcpRegions()"
                    (cancel)="cancelEditGcpRegions()"
                  ></app-geography-scope-editor>
                </div>
              </li>
            </ul>
            <p class="border-t border-slate-100 px-6 py-3 text-[11px] text-slate-400">
              Regions decide which GCP regions KernalHQ discovers and monitors resources in for a project. The default
              region is used when a resource doesn't specify one. Global services (e.g. Cloud CDN) are always covered
              regardless of the enabled regions.
            </p>
          </div>

          <!-- Add another project -->
          <div *ngIf="!isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <h2 class="text-sm font-semibold text-slate-900">Add another project</h2>
              <p class="mt-0.5 text-xs text-slate-500">Grant the same (or another) service account Viewer access to an additional project, then paste its key JSON.</p>
            </div>
            <div class="space-y-4 px-6 py-5">
              <input type="text" [(ngModel)]="gcpProjectId" placeholder="Project ID"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200" />
              <textarea rows="4" autocomplete="off" [(ngModel)]="gcpServiceAccountKey" placeholder="Service account key JSON"
                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"></textarea>
              <button
                (click)="testConnectGcp()"
                [disabled]="gcpTestLoading || !gcpProjectId || !gcpServiceAccountKey"
                class="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="gcpTestLoading" name="loader" [size]="13" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="!gcpTestLoading" name="plus" [size]="13" [strokeWidth]="2"></lucide-icon>
                Add project
              </button>
            </div>
          </div>

          <!-- Danger zone -->
          <div *ngIf="!isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-red-400">Danger Zone</p>
            </div>
            <div class="px-6 py-5">
              <p class="mb-4 text-sm text-slate-600">Removing the connection deletes every stored Google Cloud project and the service-account reference from KernalHQ.</p>
              <button
                (click)="disconnectGcp()"
                [disabled]="gcpDisconnectLoading"
                class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="gcpDisconnectLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="!gcpDisconnectLoading" name="unlink" [size]="14" [strokeWidth]="1.75"></lucide-icon>
                {{ gcpDisconnectLoading ? 'Disconnecting…' : 'Disconnect Google Cloud' }}
              </button>
            </div>
          </div>
        </ng-container>

        <!-- Error -->
        <div *ngIf="gcpState === 'error'" class="overflow-hidden rounded-xl border border-red-200 bg-red-50 px-6 py-5 text-sm text-red-700 shadow-sm">
          Failed to load the Google Cloud integration. Please refresh the page.
        </div>
      </div>

    </app-integration-category>
  </div>

  <!-- ══ Ticketing Systems ═════════════════════════════════════════════════════ -->
  <div class="py-10">
    <app-integration-category
      label="Ticketing Systems"
      description="Create and track tickets from monitored service alerts and anomalies."
    >

      <!-- ── Jira ───────────────────────────────────────────────────────────── -->
      <div class="space-y-5">
        <h3 class="text-sm font-semibold text-slate-700">Jira</h3>

        <!-- OAuth success banner -->
        <div *ngIf="jiraConnectSuccess && jiraState === 'connected'" class="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <lucide-icon name="circle-check" [size]="15" class="mt-0.5 flex-shrink-0 text-emerald-500" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-emerald-700">Jira connected successfully.</p>
        </div>

        <!-- OAuth error banner -->
        <div *ngIf="jiraConnectError" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-red-700">{{ jiraConnectError }}</p>
        </div>

        <!-- Loading -->
        <div *ngIf="jiraState === 'loading'" class="flex items-center gap-2 py-8 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading...
        </div>

        <!-- ── Not connected ──────────────────────────────────────────────── -->
        <div *ngIf="jiraState === 'not-connected'" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="flex flex-col items-center gap-5 px-8 py-10 text-center sm:flex-row sm:text-left">
            <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100">
              <lucide-icon name="list-todo" [size]="22" class="text-blue-600" [strokeWidth]="1.75"></lucide-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-slate-900">Jira Cloud</p>
              <p class="mt-0.5 text-sm text-slate-500">
                Authorize KernalHQ to read your Jira projects and issues. You'll be redirected to Atlassian to grant access.
              </p>
            </div>
            <button
              *ngIf="!isReadOnly"
              (click)="connectJira()"
              [disabled]="jiraConnectLoading"
              class="flex flex-shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <lucide-icon *ngIf="jiraConnectLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
              <lucide-icon *ngIf="!jiraConnectLoading" name="link" [size]="14" [strokeWidth]="2"></lucide-icon>
              {{ jiraConnectLoading ? 'Redirecting...' : 'Connect Jira' }}
            </button>
          </div>
        </div>

        <!-- ── Needs re-auth ──────────────────────────────────────────────── -->
        <div *ngIf="jiraState === 'needs-reauth'" class="overflow-hidden rounded-xl border border-amber-200 bg-amber-50 shadow-sm">
          <div class="flex flex-col items-center gap-4 px-6 py-6 sm:flex-row">
            <lucide-icon name="triangle-alert" [size]="22" class="flex-shrink-0 text-amber-500" [strokeWidth]="1.75"></lucide-icon>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-slate-900">Re-authorization required</p>
              <p class="mt-0.5 text-sm text-slate-600">The Jira access token has expired or been revoked. Reconnect to restore access.</p>
              <p class="mt-0.5 text-xs text-slate-400">{{ jiraConnection?.siteName || jiraConnection?.siteUrl }}</p>
            </div>
            <button
              *ngIf="!isReadOnly"
              (click)="connectJira()"
              [disabled]="jiraConnectLoading"
              class="flex flex-shrink-0 items-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
            >
              <lucide-icon *ngIf="jiraConnectLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
              {{ jiraConnectLoading ? 'Redirecting...' : 'Reconnect' }}
            </button>
          </div>
        </div>

        <!-- ── Pending: select a site ─────────────────────────────────────── -->
        <div *ngIf="jiraState === 'pending-site'" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="border-b border-slate-200 px-6 py-4">
            <h2 class="text-sm font-semibold text-slate-900">Select a Jira Site</h2>
            <p class="mt-0.5 text-xs text-slate-500">Your Atlassian account has access to multiple sites. Choose which one to connect.</p>
          </div>
          <div *ngIf="siteSelectError" class="mx-6 mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">
            <lucide-icon name="triangle-alert" [size]="13" class="mt-0.5 flex-shrink-0" [strokeWidth]="2"></lucide-icon>
            {{ siteSelectError }}
          </div>
          <div class="divide-y divide-slate-100">
            <div
              *ngFor="let site of pendingSites"
              class="flex items-center justify-between px-6 py-4 transition-colors hover:bg-slate-50"
            >
              <div class="flex items-center gap-3">
                <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <lucide-icon name="list-todo" [size]="15" class="text-blue-600" [strokeWidth]="1.75"></lucide-icon>
                </div>
                <div>
                  <p class="text-sm font-medium text-slate-900">{{ site.name }}</p>
                  <p class="text-xs text-slate-400">{{ site.url }}</p>
                </div>
              </div>
              <button
                *ngIf="!isReadOnly"
                (click)="selectSite(site.id)"
                [disabled]="!!selectingCloudId"
                class="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="selectingCloudId === site.id" name="loader" [size]="13" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                {{ selectingCloudId === site.id ? 'Connecting...' : 'Connect' }}
              </button>
            </div>
          </div>
        </div>

        <!-- ── Connected ──────────────────────────────────────────────────── -->
        <ng-container *ngIf="jiraState === 'connected' && jiraConnection">

          <!-- Status card -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-100 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Jira Cloud</p>
            </div>
            <div class="flex items-center gap-4 px-6 py-5">
              <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <lucide-icon name="list-todo" [size]="19" class="text-blue-600" [strokeWidth]="1.75"></lucide-icon>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-semibold text-slate-900">{{ jiraConnection.siteName }}</p>
                  <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Active</span>
                </div>
                <p class="mt-0.5 truncate text-xs text-slate-400">{{ jiraConnection.siteUrl }}</p>
              </div>
              <p class="flex-shrink-0 text-xs text-slate-400">Connected {{ jiraConnection.connectedAt | date:'MMM d, y' }}</p>
            </div>
          </div>

          <!-- Details -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <h2 class="text-sm font-semibold text-slate-900">Connection Details</h2>
            </div>
            <div class="divide-y divide-slate-100">
              <div class="flex justify-between px-6 py-3">
                <span class="text-xs text-slate-500">Scopes</span>
                <span class="text-xs font-medium text-slate-700">{{ jiraConnection.scopes || '—' }}</span>
              </div>
              <div class="flex justify-between px-6 py-3">
                <span class="text-xs text-slate-500">Cloud ID</span>
                <span class="font-mono text-xs text-slate-400">{{ jiraConnection.cloudId }}</span>
              </div>
              <div class="flex justify-between px-6 py-3">
                <span class="text-xs text-slate-500">Atlassian account ID</span>
                <span class="font-mono text-xs text-slate-400">{{ jiraConnection.atlassianAccountId || '—' }}</span>
              </div>
              <div class="flex justify-between px-6 py-3">
                <span class="text-xs text-slate-500">Last updated</span>
                <span class="text-xs text-slate-700">{{ jiraConnection.updatedAt | date:'MMM d, y, HH:mm' }}</span>
              </div>
            </div>
          </div>

          <!-- Default project key -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <h2 class="text-sm font-semibold text-slate-900">Default Project Key</h2>
              <p class="mt-0.5 text-xs text-slate-500">Jira ticket notifications will create issues in this project by default.</p>
            </div>
            <div class="px-6 py-5">

              <div *ngIf="jiraDefaultSuccess" class="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                <lucide-icon name="circle-check" [size]="13" class="flex-shrink-0" [strokeWidth]="2"></lucide-icon>
                Default project key saved.
              </div>

              <label class="mb-1.5 block text-xs font-medium text-slate-700" for="jiraProjectKey">Project Key</label>
              <div class="flex gap-2">
                <input
                  id="jiraProjectKey"
                  type="text"
                  [(ngModel)]="jiraDefaultDraft"
                  placeholder="OPS"
                  [disabled]="isReadOnly || jiraDefaultSaving"
                  class="flex-1 rounded-lg border px-3 py-2 text-sm font-mono placeholder-slate-400 focus:outline-none focus:ring-2"
                  [class.border-slate-200]="!jiraDefaultError"
                  [class.focus:border-violet-400]="!jiraDefaultError"
                  [class.focus:ring-violet-300]="!jiraDefaultError"
                  [class.border-red-300]="!!jiraDefaultError"
                  [class.focus:border-red-400]="!!jiraDefaultError"
                  [class.focus:ring-red-300]="!!jiraDefaultError"
                  (ngModelChange)="jiraDefaultError = null"
                />
                <button
                  *ngIf="!isReadOnly"
                  (click)="saveJiraDefaults()"
                  [disabled]="jiraDefaultSaving"
                  class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <lucide-icon *ngIf="jiraDefaultSaving" name="loader" [size]="13" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                  {{ jiraDefaultSaving ? 'Saving...' : 'Save' }}
                </button>
              </div>
              <p *ngIf="jiraDefaultError" class="mt-1.5 text-xs text-red-500">{{ jiraDefaultError }}</p>
              <p *ngIf="!jiraDefaultError" class="mt-1.5 text-xs text-slate-400">The project key from your Jira board URL, e.g. <span class="font-mono">OPS</span> or <span class="font-mono">INFRA</span></p>

            </div>
          </div>

          <!-- Danger zone -->
          <div *ngIf="!isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-red-400">Danger Zone</p>
            </div>
            <div class="px-6 py-5">
              <p class="mb-4 text-sm text-slate-600">
                Removing the connection deletes your stored Jira tokens from KernalHQ. It does <strong>not</strong> revoke the Atlassian OAuth grant — do that in
                <a href="https://id.atlassian.com/manage-profile/security/connected-apps" target="_blank" rel="noopener" class="text-violet-600 hover:underline">Atlassian security settings</a> if needed.
              </p>
              <button
                (click)="disconnectJira()"
                [disabled]="jiraDisconnectLoading"
                class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="jiraDisconnectLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="!jiraDisconnectLoading" name="unlink" [size]="14" [strokeWidth]="1.75"></lucide-icon>
                {{ jiraDisconnectLoading ? 'Disconnecting...' : 'Disconnect Jira' }}
              </button>
            </div>
          </div>

        </ng-container>

      </div>

      <!-- ── GitHub Issues ──────────────────────────────────────────────────── -->
      <div class="space-y-5">
        <h3 class="text-sm font-semibold text-slate-700">GitHub Issues</h3>

        <!-- OAuth success banner -->
        <div *ngIf="githubConnectSuccess && githubState === 'connected'" class="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <lucide-icon name="circle-check" [size]="15" class="mt-0.5 flex-shrink-0 text-emerald-500" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-emerald-700">GitHub connected successfully.</p>
        </div>

        <!-- OAuth error banner -->
        <div *ngIf="githubConnectError" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
          <p class="text-sm text-red-700">{{ githubConnectError }}</p>
        </div>

        <!-- Loading -->
        <div *ngIf="githubState === 'loading'" class="flex items-center gap-2 py-8 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading...
        </div>

        <!-- ── Not connected ──────────────────────────────────────────────── -->
        <div *ngIf="githubState === 'not-connected'" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="flex flex-col items-center gap-5 px-8 py-10 text-center sm:flex-row sm:text-left">
            <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100">
              <lucide-icon name="github" [size]="22" class="text-slate-700" [strokeWidth]="1.75"></lucide-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-slate-900">GitHub</p>
              <p class="mt-0.5 text-sm text-slate-500">
                Authorize KernalHQ to read your GitHub profile and repositories. You'll be redirected to GitHub to grant access.
              </p>
            </div>
            <button
              *ngIf="!isReadOnly"
              (click)="connectGitHub()"
              [disabled]="githubConnectLoading"
              class="flex flex-shrink-0 items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <lucide-icon *ngIf="githubConnectLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
              <lucide-icon *ngIf="!githubConnectLoading" name="github" [size]="14" [strokeWidth]="2"></lucide-icon>
              {{ githubConnectLoading ? 'Redirecting...' : 'Connect GitHub' }}
            </button>
          </div>
        </div>

        <!-- ── Connected ──────────────────────────────────────────────────── -->
        <ng-container *ngIf="githubState === 'connected' && githubConnection">

          <!-- Status card -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-100 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">GitHub</p>
            </div>
            <div class="flex items-center gap-4 px-6 py-5">
              <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                <lucide-icon name="github" [size]="19" class="text-slate-700" [strokeWidth]="1.75"></lucide-icon>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-semibold text-slate-900">{{ githubConnection.githubLogin }}</p>
                  <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Active</span>
                </div>
                <p class="mt-0.5 truncate text-xs text-slate-400">github.com/{{ githubConnection.githubLogin }}</p>
              </div>
              <p class="flex-shrink-0 text-xs text-slate-400">Connected {{ githubConnection.connectedAt | date:'MMM d, y' }}</p>
            </div>
          </div>

          <!-- Details -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <h2 class="text-sm font-semibold text-slate-900">Connection Details</h2>
            </div>
            <div class="divide-y divide-slate-100">
              <div class="flex justify-between px-6 py-3">
                <span class="text-xs text-slate-500">Scopes</span>
                <span class="text-xs font-medium text-slate-700">{{ githubConnection.scopes || '—' }}</span>
              </div>
              <div class="flex justify-between px-6 py-3">
                <span class="text-xs text-slate-500">GitHub user ID</span>
                <span class="font-mono text-xs text-slate-400">{{ githubConnection.githubUserId }}</span>
              </div>
              <div class="flex justify-between px-6 py-3">
                <span class="text-xs text-slate-500">Last updated</span>
                <span class="text-xs text-slate-700">{{ githubConnection.updatedAt | date:'MMM d, y, HH:mm' }}</span>
              </div>
            </div>
          </div>

          <!-- Default repository -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <h2 class="text-sm font-semibold text-slate-900">Default Repository</h2>
              <p class="mt-0.5 text-xs text-slate-500">GitHub Issues notifications will open issues in this repository by default.</p>
            </div>
            <div class="px-6 py-5">

              <div *ngIf="githubDefaultSuccess" class="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                <lucide-icon name="circle-check" [size]="13" class="flex-shrink-0" [strokeWidth]="2"></lucide-icon>
                Default repository saved.
              </div>

              <label class="mb-1.5 block text-xs font-medium text-slate-700" for="githubRepo">Repository</label>
              <div class="flex gap-2">
                <input
                  id="githubRepo"
                  type="text"
                  [(ngModel)]="githubDefaultDraft"
                  placeholder="owner/repository"
                  [disabled]="isReadOnly || githubDefaultSaving"
                  class="flex-1 rounded-lg border px-3 py-2 text-sm font-mono placeholder-slate-400 focus:outline-none focus:ring-2"
                  [class.border-slate-200]="!githubDefaultError"
                  [class.focus:border-violet-400]="!githubDefaultError"
                  [class.focus:ring-violet-300]="!githubDefaultError"
                  [class.border-red-300]="!!githubDefaultError"
                  [class.focus:border-red-400]="!!githubDefaultError"
                  [class.focus:ring-red-300]="!!githubDefaultError"
                  (ngModelChange)="githubDefaultError = null"
                />
                <button
                  *ngIf="!isReadOnly"
                  (click)="saveGitHubDefaults()"
                  [disabled]="githubDefaultSaving"
                  class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <lucide-icon *ngIf="githubDefaultSaving" name="loader" [size]="13" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                  {{ githubDefaultSaving ? 'Saving...' : 'Save' }}
                </button>
              </div>
              <p *ngIf="githubDefaultError" class="mt-1.5 text-xs text-red-500">{{ githubDefaultError }}</p>
              <p *ngIf="!githubDefaultError" class="mt-1.5 text-xs text-slate-400">e.g. <span class="font-mono">myorg/ops-alerts</span></p>

            </div>
          </div>

          <!-- Danger zone -->
          <div *ngIf="!isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-red-400">Danger Zone</p>
            </div>
            <div class="px-6 py-5">
              <p class="mb-4 text-sm text-slate-600">
                Removing the connection deletes your stored GitHub token from KernalHQ. It does <strong>not</strong> revoke the OAuth grant — do that in
                <a href="https://github.com/settings/applications" target="_blank" rel="noopener" class="text-violet-600 hover:underline">GitHub Authorized OAuth Apps</a> if needed.
              </p>
              <button
                (click)="disconnectGitHub()"
                [disabled]="githubDisconnectLoading"
                class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="githubDisconnectLoading" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="!githubDisconnectLoading" name="unlink" [size]="14" [strokeWidth]="1.75"></lucide-icon>
                {{ githubDisconnectLoading ? 'Disconnecting...' : 'Disconnect GitHub' }}
              </button>
            </div>
          </div>

        </ng-container>

      </div>

    </app-integration-category>
  </div>

  <!-- ══ Notification Channels ════════════════════════════════════════════════ -->
  <div class="py-10">
    <app-integration-category
      label="Notification Channels"
      description="Route alerts and status updates to your team's preferred communication tools."
    >

      <!-- ── Slack ─────────────────────────────────────────────────────────── -->
      <div class="space-y-5">
        <h3 class="text-sm font-semibold text-slate-700">Slack</h3>
        <app-webhook-config-card
          channel="slack"
          label="Slack"
          description="Route alerts and status updates to Slack channels or direct messages."
          icon="message-square"
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
          helpText="In Slack, go to your channel settings and create an Incoming Webhook. Copy the webhook URL and paste it here."
          urlPlaceholder="https://hooks.slack.com/services/..."
          [readOnly]="isReadOnly"
        ></app-webhook-config-card>
      </div>

      <!-- ── Microsoft Teams ───────────────────────────────────────────────── -->
      <div class="space-y-5">
        <h3 class="text-sm font-semibold text-slate-700">Microsoft Teams</h3>
        <app-webhook-config-card
          channel="teams"
          label="Microsoft Teams"
          description="Post alert notifications to Teams channels via a Workflows webhook."
          icon="users"
          iconBg="bg-violet-100"
          iconColor="text-violet-600"
          helpText="In Teams, go to your channel → Workflows → Post a card in a channel when a webhook request is received. Copy the generated webhook URL and paste it here. KernalHQ sends notifications as Adaptive Cards."
          urlPlaceholder="https://prod-00.westus.logic.azure.com/workflows/..."
          [readOnly]="isReadOnly"
        ></app-webhook-config-card>
      </div>

      <!-- ── Email ────────────────────────────────────────────────────────────── -->
      <div class="space-y-5">
        <h3 class="text-sm font-semibold text-slate-700">Email</h3>

        <!-- Loading -->
        <div *ngIf="emailLoadState === 'loading'" class="flex items-center gap-2 py-4 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading...
        </div>

        <!-- Error -->
        <div *ngIf="emailLoadState === 'error'" class="overflow-hidden rounded-xl border border-red-200 bg-red-50 px-6 py-5 text-sm text-red-700 shadow-sm">
          Failed to load email configuration. Please refresh the page.
        </div>

        <!-- ── Not configured ─────────────────────────────────────────────── -->
        <div *ngIf="emailLoadState === 'loaded' && !emailConfig?.configured && !emailShowForm" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="flex flex-col items-center gap-5 px-8 py-10 text-center sm:flex-row sm:text-left">
            <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100">
              <lucide-icon name="mail" [size]="22" class="text-slate-600" [strokeWidth]="1.75"></lucide-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-slate-900">Email Notifications</p>
              <p class="mt-0.5 text-sm text-slate-500">
                Select organization members to receive alert emails. Only active members may be added as recipients.
              </p>
            </div>
            <button
              *ngIf="!isReadOnly"
              (click)="startConfigureEmail()"
              class="flex flex-shrink-0 items-center gap-2 rounded-lg bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            >
              <lucide-icon name="mail" [size]="14" [strokeWidth]="2"></lucide-icon>
              Configure recipients
            </button>
          </div>
        </div>

        <!-- ── Configure form ─────────────────────────────────────────────── -->
        <div *ngIf="emailShowForm" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div class="border-b border-slate-200 px-6 py-4">
            <h2 class="text-sm font-semibold text-slate-900">Email Notification Recipients</h2>
            <p class="mt-0.5 text-xs text-slate-500">
              Only active members of this organization may receive alert emails.
            </p>
          </div>
          <div class="space-y-5 px-6 py-5">

            <!-- Member picker -->
            <div>
              <label class="mb-2 block text-xs font-medium text-slate-700">Recipients</label>
              <app-member-picker
                [members]="orgMembers"
                [selectedIds]="emailDraftIds"
                (selectedIdsChange)="emailDraftIds = $event"
                [readOnly]="false"
              ></app-member-picker>
            </div>

            <!-- Enable toggle -->
            <div class="flex items-center gap-3">
              <button
                type="button" role="switch"
                [attr.aria-checked]="emailDraftEnabled"
                (click)="emailDraftEnabled = !emailDraftEnabled"
                class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1"
                [class.bg-violet-600]="emailDraftEnabled"
                [class.bg-slate-200]="!emailDraftEnabled"
              >
                <span aria-hidden="true"
                  class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
                  [class.translate-x-4]="emailDraftEnabled"
                  [class.translate-x-0]="!emailDraftEnabled"></span>
              </button>
              <span class="text-sm text-slate-700">{{ emailDraftEnabled ? 'Enabled — send email alerts' : 'Paused — save recipients without sending' }}</span>
            </div>

            <!-- Actions -->
            <div class="flex gap-2">
              <button
                (click)="saveEmailConfig()"
                [disabled]="emailSaveState === 'saving'"
                class="flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="emailSaveState === 'saving'" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                {{ emailSaveState === 'saving' ? 'Saving…' : 'Save' }}
              </button>
              <button
                (click)="cancelEmailForm()"
                [disabled]="emailSaveState === 'saving'"
                class="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:cursor-not-allowed disabled:opacity-50"
              >Cancel</button>
            </div>
          </div>
        </div>

        <!-- ── Configured ─────────────────────────────────────────────────── -->
        <ng-container *ngIf="emailLoadState === 'loaded' && emailConfig?.configured && !emailShowForm">

          <!-- Status card -->
          <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-100 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Email Notifications</p>
            </div>
            <div class="flex items-center gap-4 px-6 py-5">
              <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                <lucide-icon name="mail" [size]="19" class="text-slate-600" [strokeWidth]="1.75"></lucide-icon>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-semibold text-slate-900">
                    {{ emailConfig!.recipientIds.length }} recipient{{ emailConfig!.recipientIds.length !== 1 ? 's' : '' }}
                  </p>
                  <span
                    class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    [class.bg-emerald-100]="emailConfig!.enabled"
                    [class.text-emerald-700]="emailConfig!.enabled"
                    [class.bg-slate-100]="!emailConfig!.enabled"
                    [class.text-slate-500]="!emailConfig!.enabled"
                  >{{ emailConfig!.enabled ? 'Active' : 'Paused' }}</span>
                </div>
                <p class="mt-0.5 truncate text-xs text-slate-400">{{ emailRecipientNames() }}</p>
              </div>
              <div class="flex flex-shrink-0 items-center gap-2">
                <button
                  *ngIf="!isReadOnly"
                  (click)="toggleEmailEnabled()"
                  [disabled]="emailSaveState === 'saving'"
                  class="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >{{ emailConfig!.enabled ? 'Pause' : 'Enable' }}</button>
                <button
                  *ngIf="!isReadOnly"
                  (click)="startConfigureEmail()"
                  class="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >Edit</button>
              </div>
            </div>
          </div>

          <!-- Danger zone -->
          <div *ngIf="!isReadOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-200 px-6 py-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-red-400">Danger Zone</p>
            </div>
            <div class="px-6 py-5">
              <p class="mb-4 text-sm text-slate-600">
                Removing the email configuration deletes the recipient list from KernalHQ. Service and item-level email override policies will lose their destination and alerts will not be sent.
              </p>
              <button
                (click)="removeEmailConfig()"
                [disabled]="emailRemoveState === 'removing'"
                class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="emailRemoveState === 'removing'" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                <lucide-icon *ngIf="emailRemoveState !== 'removing'" name="unlink" [size]="14" [strokeWidth]="1.75"></lucide-icon>
                {{ emailRemoveState === 'removing' ? 'Removing…' : 'Remove email config' }}
              </button>
            </div>
          </div>

        </ng-container>
      </div>

    </app-integration-category>
  </div>

</div>
